/**
 * Drone Operator Coverage detail panel (L.2.2, §3B.6). UNIFIED with the marketplace
 * fleet (#343): a coverage pin IS a drone_assets row, so this shows the real fleet
 * fields (operator, platform, sensors, service radius, reachability) AND the pricing —
 * "$0 · Government asset" for a government / public-service operator, else "Quote on
 * accept" for a commercial one. Header (name + readiness badge + operator), a coverage
 * section, the derived on-station estimate for the current incident, the sensor loadout,
 * and the recommend / "Request live eyes" affordances (logged → forward, never a direct
 * tasking write).
 */
import { useEffect, useState } from 'react';
import { eyesOnEnduranceCheck } from '@tsr/shared-types';
import { type DroneCoverageFeature, type DroneMissionView } from './api';
import {
  BVLOS_DISPLAY_CAP_KM,
  DRONE_AMBIENT_COLOR,
  coverageRadiusKm,
  deriveReach,
  displayCoverageRadiusKm,
  dronePriceLabel,
  isCoverageOverProvisioned,
  platformLabel,
  sensorLabel,
} from './droneCoverage';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { DroneMissionsPanel } from './DroneMissionsPanel';
import { DEFAULT_EYES_ON_MINUTES, RequestLiveEyes } from './RequestLiveEyes';

// Keyed to the §3B.6 readiness band (Phase 4c): a drone-mission-derived 'committed' takes
// precedence over the asset's own availability_status (available / maintenance / unavailable,
// DRONE_AVAILABILITY_STATUSES, #343). 'committed' = tasked elsewhere right now, so the asset is
// NOT offered as ready. Default fallback stays for forward-compat.
function readinessBadge(readiness: string): { label: string; cls: string } {
  switch (readiness) {
    case 'available':
      return { label: 'Available', cls: 'bg-emerald-500/20 text-emerald-300' };
    case 'committed':
      return { label: 'Committed', cls: 'bg-amber-500/20 text-amber-300' };
    case 'maintenance':
      return { label: 'Maintenance', cls: 'bg-slate-500/20 text-slate-300' };
    case 'unavailable':
      return { label: 'Unavailable', cls: 'bg-slate-600/40 text-slate-300' };
    default:
      return { label: readiness, cls: 'bg-slate-600/40 text-slate-300' };
  }
}

export function DroneCoverageBody({
  drone,
  incidentPoint,
  incidentId = null,
  onSelectMission,
}: {
  drone: DroneCoverageFeature;
  /** The current incident context (last-selected incident) for the on-station estimate. */
  incidentPoint: { lat: number; lon: number } | null;
  /** Coordination re-arch Phase 2 (Drone Ruling 3) — the working incident's id, so a
   *  drone tasked from this panel links to THAT incident (it then appears in the
   *  incident's resource thread) instead of the prior hardcoded null. Null when no
   *  incident is in context → the mission is created unlinked, as before. */
  incidentId?: string | null;
  /** #322 — open a mission's lifecycle + live-feed panel from a missions-panel row. */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  const p = drone.properties;
  const [lon, lat] = drone.geometry.coordinates;
  const [busy, setBusy] = useState(false);
  // Bumped after a mission is created from "Request live eyes" so the missions panel reloads.
  const [missionsKey, setMissionsKey] = useState(0);
  // 4c.1 — the requested eyes-on duration is owned HERE (single source of truth) and handed to the
  // RequestLiveEyes input below as a controlled value, so the §3B.6 endurance check reflects the
  // operator's LIVE watch-window input rather than a stale copy. Seeded to the same default.
  const [eyesOnMinutes, setEyesOnMinutes] = useState<number>(DEFAULT_EYES_ON_MINUTES);
  // §3B.6 / §G honesty (#345): does the current requested eyes-on outrun THIS asset's endurance?
  const enduranceWarn = eyesOnEnduranceCheck(eyesOnMinutes, p.flight_endurance_min ?? null);
  const badge = readinessBadge(p.readiness);
  // True stated radius (drives reachability) vs the realistic BVLOS DISPLAY radius (#374).
  const radiusKm = coverageRadiusKm(p.service_radius_km);
  const displayRadiusKm = displayCoverageRadiusKm(p.service_radius_km);
  const overProvisioned = isCoverageOverProvisioned(p.service_radius_km);

  useEffect(() => {
    logAction('view_drone_coverage_detail', { drone_id: drone.id, name: p.name });
  }, [drone.id, p.name]);

  // Phase 4c — pass the asset's response-time telemetry so deriveReach can compute the REAL
  // ETA-to-on-station (launch + transit) via the shared formula, not the coarse nominal cruise.
  const reach = incidentPoint
    ? deriveReach(p.service_radius_km, lat, lon, incidentPoint.lat, incidentPoint.lon, {
        cruiseSpeedKmh: p.cruise_speed_kmh,
        launchDelayMinutes: p.launch_delay_minutes,
      })
    : null;

  const recommend = (): void => {
    setBusy(true);
    logAction('recommend_drone_inspection', {
      drone_id: drone.id,
      name: p.name,
      incident: incidentPoint,
      est_transit_min: reach?.estTransitMin ?? null,
    });
    // recommend → forward only: no direct tasking write (PEOC coordinate, not
    // command). The audit log IS the record (like the SITREP forward stub).
    void getSessionId();
    showToast(
      `Drone inspection recommended — ${p.name}. Status: pending review (no tasking sent).`,
      { variant: 'success' },
    );
    setBusy(false);
  };

  return (
    <div className="px-4 py-4 space-y-4" data-testid="drone-coverage-body">
      <div className="border-l-2 pl-3" style={{ borderColor: DRONE_AMBIENT_COLOR }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Drone Operator Coverage</p>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">{p.name}</h2>
          <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
        {p.operator_name && <p className="text-xs text-slate-400 mt-0.5">{p.operator_name}</p>}
      </div>

      {/* Pricing (#343): government / public-service = $0; commercial = quote on accept. */}
      <div
        className={`rounded px-3 py-2 text-sm font-medium ${
          p.government_owned
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
            : 'bg-slate-700/30 border border-slate-600/40 text-slate-200'
        }`}
        data-testid="drone-price"
      >
        {dronePriceLabel(p.government_owned)}
        {p.government_owned && (
          <span className="block text-[10px] font-normal text-emerald-300/80 mt-0.5">
            Public-service operator — no charge for this asset.
          </span>
        )}
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Coverage</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Operator</dt>
          <dd className="font-medium truncate">{p.operator_name ?? '—'}</dd>
          <dt className="text-slate-400">Platform</dt>
          <dd className="font-mono">{platformLabel(p.platform_category)}</dd>
          <dt className="text-slate-400">Service radius</dt>
          <dd className="font-mono" data-testid="drone-service-radius">
            {displayRadiusKm > 0 ? `${displayRadiusKm} km` : '—'}
          </dd>
          {p.downlink_type && (
            <>
              <dt className="text-slate-400">Downlink</dt>
              <dd className="font-mono uppercase">{p.downlink_type}</dd>
            </>
          )}
        </dl>
        {overProvisioned && (
          <p
            className="mt-1.5 text-[11px] text-amber-200/90 leading-relaxed"
            data-testid="drone-bvlos-note"
          >
            Provider lists {radiusKm} km — shown capped at {BVLOS_DISPLAY_CAP_KM} km, the typical
            BVLOS detect-and-avoid range. Coverage beyond that is aspirational (needs a waiver or
            relay).
          </p>
        )}
      </section>

      {/* §3B.6 Response-time block (Phase 4c): the headline derived ETA-to-on-station for the
          selected incident + the four decision fields (launch · transit radius · flight
          endurance · area/sortie). The ETA = launch + transit (computeDroneEtaMinutes); flight
          endurance is the eyes-on window (4b vocabulary), never summed into the ETA. */}
      <section className="border-t border-slate-800 pt-3" data-testid="drone-response-time">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Response time</h3>

        {!incidentPoint ? (
          <p className="text-xs text-slate-400 mb-2">
            Select an incident to see the ETA to on-station + reachability from this provider.
          </p>
        ) : reach && reach.reachable ? (
          <div
            className="rounded bg-violet-500/10 border border-violet-500/30 px-3 py-2 mb-2"
            data-testid="drone-eta-headline"
          >
            <p className="text-2xl font-semibold text-violet-200">
              {reach.etaMinutes != null
                ? `~${reach.etaMinutes} min`
                : reach.estTransitMin != null
                  ? `~${reach.estTransitMin} min`
                  : 'Reachable'}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {reach.etaMinutes != null
                ? 'ETA to on-station (launch + transit) · '
                : reach.estTransitMin != null
                  ? 'ETA to on-station — est. transit only · '
                  : ''}
              {reach.distanceKm} km from base · {reach.slackKm} km range to spare
            </p>
            {reach.distanceKm > BVLOS_DISPLAY_CAP_KM && (
              <p className="text-[11px] text-amber-200/90 mt-1" data-testid="drone-bvlos-reach-note">
                Beyond the {BVLOS_DISPLAY_CAP_KM} km typical BVLOS range — provider’s stated reach,
                aspirational (needs a waiver or relay).
              </p>
            )}
          </div>
        ) : (
          <div className="rounded bg-slate-700/30 border border-slate-600/40 px-3 py-2 mb-2">
            <p className="text-sm text-slate-300">Out of reach</p>
            {/* Frame against the provider's TRUE stated reach — never re-label the raw radius as
                the (capped) "service radius" the header above shows, to avoid two numbers under
                one label for an over-provisioned asset. */}
            <p className="text-[11px] text-slate-400 mt-0.5">
              {reach?.distanceKm} km from base · beyond the provider’s {radiusKm} km stated reach.
            </p>
          </div>
        )}

        {/* The four §3B.6 decision drivers — always shown (the asset's own response profile,
            incident-independent). "—" when the asset carries no telemetry yet. */}
        <dl className="grid grid-cols-2 gap-y-1 text-sm" data-testid="drone-response-fields">
          <dt className="text-slate-400">Launch time</dt>
          <dd className="font-mono" data-testid="drone-launch-time">
            {p.launch_delay_minutes != null ? `~${p.launch_delay_minutes} min spin-up` : '—'}
          </dd>
          <dt className="text-slate-400">Transit radius</dt>
          <dd className="font-mono" data-testid="drone-transit-radius">
            {displayRadiusKm > 0 ? `${displayRadiusKm} km` : '—'}
          </dd>
          <dt className="text-slate-400">Flight endurance</dt>
          <dd
            className={`font-mono ${enduranceWarn.exceeds ? 'text-amber-200' : ''}`}
            data-testid="drone-flight-endurance"
          >
            {p.flight_endurance_min != null ? `${p.flight_endurance_min} min eyes-on` : '—'}
          </dd>
          <dt className="text-slate-400">Area / sortie</dt>
          <dd className="font-mono" data-testid="drone-area-coverage">
            {/* double precision — round on render so a fractional portal value never shows float noise. */}
            {p.area_coverage_sqkm != null ? `${Math.round(p.area_coverage_sqkm * 10) / 10} km²` : '—'}
          </dd>
        </dl>

        {/* 4c.1 / §G honesty (#345): the requested eyes-on outruns this drone's endurance — say so
            (the shortfall + the relay/sortie reality), never silently accept an impossible watch. */}
        {enduranceWarn.exceeds && (
          <p
            className="mt-1.5 text-[11px] text-amber-200/90 leading-relaxed"
            data-testid="drone-endurance-warning-coverage"
          >
            ⚠ Your {eyesOnMinutes}-min eyes-on request exceeds this drone’s{' '}
            {enduranceWarn.enduranceMin}-min endurance — the full watch needs{' '}
            {enduranceWarn.sortiesNeeded} sorties with turnaround gaps, or a relay of aircraft for
            continuous coverage.
          </p>
        )}
      </section>

      {p.sensors.length > 0 && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Sensors</h3>
          <div className="flex flex-wrap gap-1.5">
            {p.sensors.map((s) => (
              <span
                key={s}
                className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200"
              >
                {sensorLabel(s)}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* #348c — Request live eyes is the real marketplace entry (match → mission),
          so it leads. "Recommend Drone Inspection" below is the audit-only log.
          Phase 2 (Drone R3): when an incident is in context, the AO is that incident
          AND the created mission links to it (incidentId), so the drone appears in the
          incident's resource thread — no longer the hardcoded null. */}
      <RequestLiveEyes
        ao={incidentPoint ?? { lat, lon }}
        aoLabel={incidentPoint ? 'the selected incident' : 'this provider'}
        incidentId={incidentPoint ? incidentId : null}
        onMissionCreated={() => setMissionsKey((k) => k + 1)}
        eyesOnMinutes={eyesOnMinutes}
        onEyesOnMinutesChange={setEyesOnMinutes}
      />

      {/* #348c — demoted secondary affordance: logs a recommendation for PEOC review;
          it does NOT task anyone. To actually task an operator, use Request live eyes. */}
      <section className="border-t border-slate-800 pt-3">
        <button
          type="button"
          disabled={busy}
          onClick={recommend}
          className="w-full rounded border border-slate-600/70 bg-slate-700/40 hover:bg-slate-700/70 disabled:opacity-50 px-3 py-1.5 text-[13px] font-medium text-slate-200"
        >
          {busy ? 'Logging…' : 'Log inspection recommendation (audit only)'}
        </button>
        <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
          Records a recommendation for PEOC review — forward only, nothing is tasked.
          To task an operator, use <span className="text-slate-300">Request live eyes</span> above.
        </p>
      </section>

      <DroneMissionsPanel refreshKey={missionsKey} onSelectMission={onSelectMission} />

      {p.illustrative && (
        <div className="border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2 text-[11px] text-amber-200/90 leading-relaxed">
          Illustrative — pending a data partnership. Coverage + response-time figures
          are representative, not a live operational feed.
        </div>
      )}
    </div>
  );
}
